import { Country, CountryTransfert } from "./database";

/** Noms des pays en français, par code ISO. À défaut, on garde le nom renvoyé par l'API. */
export const COUNTRY_NAMES_FR: Record<string, string> = {
  CA: "Canada",
  CM: "Cameroun",
  CI: "Côte d’Ivoire",
  BJ: "Bénin",
  TG: "Togo",
  BF: "Burkina Faso",
  CD: "RD Congo",
  GA: "Gabon",
  ZA: "Afrique du Sud",
  CN: "Chine",
  SN: "Sénégal",
  ML: "Mali",
  NE: "Niger",
  GN: "Guinée",
  CG: "Congo",
  TD: "Tchad",
  CF: "Centrafrique",
  GQ: "Guinée équatoriale",
  FR: "France",
  US: "États-Unis",
};

/** Ordre d'affichage du tableau quotidien (identique au modèle Excel). Les autres pays suivent par ordre alphabétique. */
export const COUNTRY_ORDER = ["CA", "CM", "CI", "BJ", "TG", "BF", "CD", "GA", "ZA", "CN"];

export const countryNameFr = (country?: Partial<Country> | null): string => {
  if (!country) return "—";
  return (country.code && COUNTRY_NAMES_FR[country.code]) || country.name || "—";
};

const orderIndex = (country?: Partial<Country> | null) => {
  const i = country?.code ? COUNTRY_ORDER.indexOf(country.code) : -1;
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

export const compareCountries = (a?: Partial<Country> | null, b?: Partial<Country> | null) => {
  const diff = orderIndex(a) - orderIndex(b);
  if (diff !== 0) return diff;
  return countryNameFr(a).localeCompare(countryNameFr(b), "fr");
};

export interface RateGroup {
  senderId: number;
  sender?: Country;
  items: CountryTransfert[];
}

/** Regroupe les corridors par pays de départ, triés selon l'ordre du modèle. */
export const groupRatesBySender = (rates: CountryTransfert[]): RateGroup[] => {
  const map = new Map<number, RateGroup>();
  for (const r of rates) {
    const key = Number(r.country_sender_id);
    if (!map.has(key)) map.set(key, { senderId: key, sender: r.country_sender, items: [] });
    map.get(key)!.items.push(r);
  }
  const groups = [...map.values()].sort((a, b) => compareCountries(a.sender, b.sender));
  groups.forEach((g) => g.items.sort((a, b) => compareCountries(a.country_destination, b.country_destination)));
  return groups;
};

/**
 * Formate un nombre à la française sans zéros inutiles ("0.0030" -> "0,003", "2000" -> "2 000").
 * Les espaces insécables de fr-FR sont remplacées par des espaces simples (mieux gérées par les polices PDF).
 */
export const formatNumberFr = (value: unknown, maxDigits = 6): string => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("fr-FR", { maximumFractionDigits: maxDigits }).replace(/[\u202F\u00A0]/g, " ");
};

/** "1 CAD = 426 XAF" */
export const formatRateLabel = (
  rate: CountryTransfert | { rate: string; country_sender?: Country; country_destination?: Country }
) => {
  const from = rate.country_sender?.devise ?? "";
  const to = rate.country_destination?.devise ?? "";
  const value = formatNumberFr(rate.rate);
  if (!value) return "";
  return `1 ${from} = ${value} ${to}`.replace(/\s+/g, " ").trim();
};

/** Accepte "0,0025" ou "0.0025" ; retourne la chaîne normalisée avec un point, ou null si invalide. */
export const normalizeRateInput = (input: string): string | null => {
  const cleaned = input.trim().replace(/\s/g, "").replace(",", ".");
  if (!cleaned) return null;
  if (!/^\d*\.?\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0) return null;
  return cleaned;
};

/** XAF et XOF s'affichent "fcfa", les autres devises gardent leur code. */
export const currencyLabel = (devise?: string) => {
  if (!devise) return "";
  return devise === "XAF" || devise === "XOF" ? "fcfa" : devise;
};

/**
 * Frais d'envoi d'un corridor. Tant que l'API ne renvoie pas de champ "fees",
 * on affiche "0 fcfa". Sinon : montant + devise du pays de départ.
 */
export const formatFeesLabel = (fees: unknown, senderDevise?: string): string => {
  const value = formatNumberFr(fees, 2);
  if (!value) return "0 fcfa";
  return `${value} ${currencyLabel(senderDevise)}`.trim();
};
